import { CircleAlert, CircleCheck, Info, X } from 'lucide-react'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { cn } from '../cn'

export type ToastTone = 'success' | 'error' | 'info'

export interface ToastOptions {
  message: ReactNode
  title?: ReactNode
  tone?: ToastTone
  /** Auto-dismiss delay in ms. Errors default to staying until dismissed (UXR-020). */
  duration?: number | null
}

interface ToastEntry extends ToastOptions {
  id: number
  tone: ToastTone
}

interface ToastApi {
  toast: (options: ToastOptions) => number
  dismiss: (id: number) => void
}

const ToastContext = createContext<ToastApi | null>(null)

const DEFAULT_DURATION = 6000

const TONE_STYLE: Record<ToastTone, { bar: string; icon: ReactNode }> = {
  success: { bar: 'bg-positive', icon: <CircleCheck className="text-positive" /> },
  error: { bar: 'bg-danger', icon: <CircleAlert className="text-danger" /> },
  info: { bar: 'bg-teal', icon: <Info className="text-teal" /> },
}

/** Mount once near the root. Success/info toasts are polite; errors are assertive and sticky. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([])
  const nextId = useRef(1)

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const toast = useCallback((options: ToastOptions) => {
    const id = nextId.current++
    setToasts((current) => [
      ...current.slice(-3),
      { ...options, id, tone: options.tone ?? 'success' },
    ])
    return id
  }, [])

  const api = useMemo(() => ({ toast, dismiss }), [toast, dismiss])
  const polite = toasts.filter((entry) => entry.tone !== 'error')
  const assertive = toasts.filter((entry) => entry.tone === 'error')

  return (
    <ToastContext value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[70] flex flex-col items-center gap-2 p-4 sm:items-end sm:p-6">
        <div
          role="alert"
          aria-live="assertive"
          className="flex w-full flex-col items-center gap-2 sm:items-end"
        >
          {assertive.map((entry) => (
            <ToastCard key={entry.id} entry={entry} onDismiss={dismiss} />
          ))}
        </div>
        <div
          role="status"
          aria-live="polite"
          className="flex w-full flex-col items-center gap-2 sm:items-end"
        >
          {polite.map((entry) => (
            <ToastCard key={entry.id} entry={entry} onDismiss={dismiss} />
          ))}
        </div>
      </div>
    </ToastContext>
  )
}

function ToastCard({ entry, onDismiss }: { entry: ToastEntry; onDismiss: (id: number) => void }) {
  const [paused, setPaused] = useState(false)
  const duration =
    entry.duration === undefined
      ? entry.tone === 'error'
        ? null
        : DEFAULT_DURATION
      : entry.duration

  useEffect(() => {
    if (duration === null || paused) return
    const timer = window.setTimeout(() => onDismiss(entry.id), duration)
    return () => window.clearTimeout(timer)
  }, [duration, paused, entry.id, onDismiss])

  const style = TONE_STYLE[entry.tone]
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: hover/focus only pause the auto-dismiss timer
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className="pointer-events-auto relative flex w-full max-w-[26rem] animate-toast-in gap-3 overflow-hidden rounded-lg border border-line bg-surface py-3.5 pr-2 pl-4 text-sm text-ink shadow-raised"
    >
      <span aria-hidden="true" className={cn('absolute inset-y-0 left-0 w-1', style.bar)} />
      <span aria-hidden="true" className="mt-0.5 shrink-0 [&>svg]:size-[18px]">
        {style.icon}
      </span>
      <div className="min-w-0 flex-1 leading-relaxed">
        {entry.title ? <p className="font-semibold">{entry.title}</p> : null}
        <div className={entry.title ? 'text-ink-soft' : undefined}>{entry.message}</div>
      </div>
      <button
        type="button"
        onClick={() => onDismiss(entry.id)}
        aria-label="Dismiss notification"
        className="-my-1 inline-flex size-8 shrink-0 items-center justify-center rounded-md text-sage hover:bg-wash hover:text-ink"
      >
        <X aria-hidden="true" className="size-4" />
      </button>
    </div>
  )
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext)
  if (!api) throw new Error('useToast must be used inside <ToastProvider>')
  return api
}
